import React from "react";
import axios from "axios";
import { notifications } from '@mantine/notifications';
import { useLoginContext } from "../../../../context/loginContext";

import { Document, Page, Text, View, StyleSheet, BlobProvider, Image as ImagePdf, Font } from '@react-pdf/renderer';
import { Button, Group, Box, Text as TextMantine, useMantineTheme, Loader, ScrollArea, Modal } from '@mantine/core';
import { saveAs } from 'file-saver';
import { IconDownload, IconFileDescription } from '@tabler/icons-react';

import { utils, BE_API_ONE, pusatCabangActive } from "../../../../utils/globalVariables";
import { numberWithComma, sqlDateToHumanDate, sqlDateToIsoDate, isoDateToHumanDate, isoDateToSqlDate, capitalizeEachWord } from "../../../../utils/globalFunctions";

import KopDocument from "./KopDocument";
import PickerDate from "../../../general/PickerDate";
import ModalResponse from "../../../general/ModalResponse";

import ttdPerusahaan from "../../../../assets/images/ttd_perusahaan.png";
import calibriBold from "../../../../assets/fonts/calibri-bold.ttf";
import calibriRegular from "../../../../assets/fonts/calibri-regular.ttf";

const widthQty = '35px';
const widthNo = '25px';
const widthHargaSatuan = '100px';
const widthJumlah = '110px';

// flexBasis: 1, flexGrow: 1, flex: 1,

const styles = StyleSheet.create({
  flexRow: {
    display: 'flex',
    flexDirection: "row",
    flexWrap: "nowrap",
  },
  flexRowSub: {
    flexBasis: 'auto',
    flexGrow: '1',
  },

  containerJudul: {
    display: 'flex',
    flexDirection: "row",
    flexWrap: "nowrap",
    borderBottom: '1px solid #000'
  },
  no: {
    fontFamily: 'calibri-bold',
    textAlign: 'center',
    padding: '4px',
    borderRight: '1px solid #000',
    width: widthNo
  },
  qty: {
    fontFamily: 'calibri-bold',
    textAlign: 'center',
    paddingTop: '4px',
    paddingBottom: '4px',
    borderRight: '1px solid #000',
    width: widthQty
  },
  itemJasa: {
    fontFamily: 'calibri-bold',
    textAlign: 'center',
    paddingTop: '4px',
    paddingBottom: '4px',
    borderRight: '1px solid #000',
    flexBasis: 'auto',
    flexGrow: 1,
    flex: 1
  },
  xxx: {
    // display: 'flex',
    // flexDirection: 'row',
    // flexWrap: 'wrap',
    flexBasis: 1,
    flexGrow: 1,
    flex: 1,
  },
  hargaSatuan: {
    fontFamily: 'calibri-bold',
    textAlign: 'center',
    paddingTop: '4px',
    paddingBottom: '4px',
    borderRight: '1px solid #000',
    width: widthHargaSatuan
  },
  jumlah: {
    fontFamily: 'calibri-bold',
    textAlign: 'center',
    paddingTop: '4px',
    paddingBottom: '4px',
    width: widthJumlah
  },
});

Font.register({ family: 'calibri-bold', src: calibriBold });
Font.register({ family: 'calibri-regular', src: calibriRegular });

const Points = ({ stateDesc = '', stateIsBold = false }) => {
  return (
    <View style={{ marginTop: '1px' }}>
      <View style={styles.flexRow}>
        <View style={{ width: '4px', marginRight: '2px' }}>
          <Text style={{ fontSize: '10px' }}>-</Text>
        </View>

        <View style={styles.flexRowSub}>
          <Text
            style={{
              fontSize: '10px',
              fontFamily: stateIsBold ? 'calibri-bold' : 'calibri-regular'
            }}
          >
            {stateDesc}
          </Text>
        </View>
      </View>
    </View>
  )
}

const fnSubTotal = (stateData) => {
  const totalBeforeDiscount = stateData.reduce((accumulator, currentValue) => {
    return accumulator + Number(currentValue.jumlah);
  }, 0);

  return totalBeforeDiscount;
}

const fnTotal = (stateData, discount = 0, downPayment = 0, stateDataProject) => {
  const dvId = stateDataProject[0]?.diskon_voucher_id;
  const dvFlag = stateDataProject[0]?.diskon_voucher_flag; /* voucher | khusus */
  const dvType = stateDataProject[0]?.diskon_voucher_type; /* percent | rp */
  const dvValue = stateDataProject[0]?.diskon_voucher_value;
  const dvDeducted = stateDataProject[0]?.diskon_deducted_from;
  const dvCode = stateDataProject[0]?.diskon_voucher_code;

  const totalBeforeDiscountItem = stateData.reduce((accumulator, currentValue) => {
    return accumulator + (currentValue.flag === 'item' ? Number(currentValue.jumlah) : 0);
  }, 0);

  const totalBeforeDiscountJasa = stateData.reduce((accumulator, currentValue) => {
    return accumulator + (currentValue.flag === 'jasa' ? Number(currentValue.jumlah) : 0);
  }, 0);

  const subTotal = totalBeforeDiscountItem + totalBeforeDiscountJasa;

  let potonganRes = 0;
  let besarPotongan = 0;

  if (dvId && dvValue && dvType && dvDeducted && dvFlag) {
    if (dvType === utils.discountType.percent.value) {
      besarPotongan = Number(dvValue) / 100 * (dvDeducted === utils.discountDeductedFrom.jasa.value ? totalBeforeDiscountJasa : subTotal);
    } else {
      besarPotongan = Number(dvValue);
    }

    if (dvFlag === utils.discountFlag.discount.value) {
      potonganRes = besarPotongan;
    } else if ((dvFlag === utils.discountFlag.voucher.value) && dvCode) {
      potonganRes = besarPotongan;
    } else {
      potonganRes = 0;
    }
  }

  const totalAfterDiscount = totalBeforeDiscountItem + totalBeforeDiscountJasa - Number(discount);
  const totalAfterDownPayment = totalAfterDiscount - Number(downPayment);
  const totalAfterPotongan = totalAfterDownPayment - potonganRes;

  return {
    diskonVoucher: potonganRes,
    total: totalAfterPotongan
  };
};

const Invoice = ({
  stateData,
  stateDataProject,
  stateCaption,
  stateProjectId,
  stateCustomerName,
  stateCustomerContact,
  stateTanggalNota,
  stateLoading,
  fnSetIdleData,
  fnGetData,
  stateDT
}) => {

  const mountedRef = React.useRef(true);
  const theme = useMantineTheme();
  const { loginContext, handleSetLoginContext } = useLoginContext();

  const notificationId = React.useRef({
    errorSubmitData: 'Invoice-1',
  })

  const [data, setData] = React.useState({
    submitLoading: false,

    firstPrintInvModal: false,
    firstPrintInvTanggalClicked: null,
    firstPrintInvTanggalSelected: null,
    firstPrintInvTanggalPermanent: null,
    firstPrintInvSubmitLoading: false,

    modalResponse: {
      show: false,
      type: utils.modalResponse.warning,
      hideOnClickOutSide: false,
      content: ''
    }
  })

  React.useEffect(() => {
    return () => {
      mountedRef.current = false;
    }
  }, [])

  React.useEffect(() => {
    let resTgl = null;

    if (stateDT.jenisDokumen === utils.jenisDokumen.buktiBayar.value) {
      resTgl = Array.isArray(stateDataProject) && (stateDataProject.length > 0) ? (stateDataProject[0].tanggal_pelunasan ? sqlDateToIsoDate(stateDataProject[0].tanggal_pelunasan, null) : new Date()) : new Date();
    } else {
      resTgl = Array.isArray(stateDataProject) && (stateDataProject.length > 0) ? (stateDataProject[0].tanggal_pertama_cetak_inv ? sqlDateToIsoDate(stateDataProject[0].tanggal_pertama_cetak_inv, null) : null) : null;
    }

    setData(d => ({
      ...d,
      firstPrintInvTanggalSelected: resTgl,
      firstPrintInvTanggalPermanent: resTgl,
    }))
  }, [stateDataProject, stateDT.jenisDokumen])

  const handleFirstPrintInvDate = () => {
    setData(d => ({ ...d, firstPrintInvSubmitLoading: true }))

    const formDT = new FormData();
    const formDTHeader = { 'Content-Type': 'application/json' }

    formDT.append('json_token', loginContext.syswebapp);
    formDT.append('project_id', stateDT.projectId);
    formDT.append('project_process', stateDT.projectStep);
    formDT.append('project_is_cancel', stateDT.projectIsCancel);
    formDT.append('first_print_inv_date', isoDateToSqlDate(data.firstPrintInvTanggalClicked, ''));

    axios({
      method: 'post',
      url: BE_API_ONE + '/set_tgl_cetak_pertama_inv',
      data: formDT,
      headers: formDTHeader,
    }).then((response) => {
      if (!mountedRef.current) return null;

      setData(d => ({ ...d, firstPrintInvSubmitLoading: false, firstPrintInvModal: false }));

      if (response.data.flag_status === 'isset') {
        setData(d => ({
          ...d,
          firstPrintInvTanggalSelected: sqlDateToIsoDate(response.data.data, null),
          firstPrintInvTanggalPermanent: sqlDateToIsoDate(response.data.data, null),
        }))
      }

      if (response.data.flag_status === 'error') {
        notifications.show({
          id: notificationId.current.errorSubmitData,
          title: utils.notification.warning.caption,
          message: response.data.pesan,
          color: utils.notification.warning.color,
          icon: utils.notification.warning.icon,
          withBorder: true,
          autoClose: true,
        })
      }

      if (response.data.flag_status === 'no-data') {
        setData(d => ({
          ...d,
          modalResponse: {
            ...d.modalResponse,
            type: utils.modalResponse.warning,
            hideOnClickOutSide: true,
            content: 'Data tidak ditemukan.',
            show: true
          }
        }))
      }

      if (response.data.flag_status === 'invalid-json-token') {
        handleSetLoginContext(null);
      }

      if (response.data.flag_status === 'user-inactive') {
        handleSetLoginContext(null);
      }
    }).catch(error => {
      if (!mountedRef.current) return null;

      setData(d => ({ ...d, firstPrintInvSubmitLoading: false, firstPrintInvModal: false }))

      notifications.show({
        id: notificationId.current.errorSubmitData,
        title: utils.notification.warning.caption,
        message: error.message,
        color: utils.notification.warning.color,
        icon: utils.notification.warning.icon,
        withBorder: true,
        autoClose: true,
      })
    })
  }

  // const handleTanggalDokumen = (stateJenisDokumen) => {
  //   const tglLunas = Array.isArray(stateDataProject) && (stateDataProject.length > 0) ? (stateDataProject[0].tanggal_pelunasan ? stateDataProject[0].tanggal_pelunasan : isoDateToSqlDate(new Date(), '')) : isoDateToSqlDate(new Date(), '');

  //   if (stateJenisDokumen === utils.jenisDokumen.buktiBayar.value) {
  //     setData(d => ({ ...d, firstPrintInvTanggalSelected: tglLunas }))
  //   } else {
  //     isoDateToSqlDate(data.firstPrintInvTanggalSelected, null)
  //   }
  // }

  const DocPdf = (
    <Document>
      <Page
        orientation='landscape'
        size="A5"
        // size={[595.28, 419.53]}
        // size={[140, 210]}

        style={{
          padding: '18px 24px 18px 24px',
          fontSize: '11px',
          fontFamily: 'calibri-regular'
        }}
      >
        <KopDocument
          stateCaption={stateCaption}
          stateCustomerContact={stateCustomerContact}
          stateCustomerName={stateCustomerName}
          stateProjectId={stateProjectId}
          stateTanggalNota={isoDateToSqlDate(data.firstPrintInvTanggalSelected, null)}
        />

        <View style={styles.flexRow}>
          <Text style={{ fontFamily: 'calibri-bold' }}>Tanggal Tanda Terima Barang : </Text>
          <Text>
            {sqlDateToHumanDate({
              stateDate: stateTanggalNota,
              stateWithTime: false,
              stateLang: 'id',
              stateIsShort: false,
              stateDay: false,
              stateDefaultValue: ' - ',
              stateDateType: 'day',
              stateWithSecond: false
            })}
          </Text>
        </View>

        <View style={{ borderLeft: '1px solid #000', borderRight: '1px solid #000', borderBottom: '1px solid #000', marginTop: '2px' }}>
          <View fixed style={[styles.containerJudul, { borderTop: '1px solid #000', }]}>
            <Text style={styles.no}>
              No
            </Text>

            <Text style={styles.itemJasa}>
              Item/Jasa
            </Text>

            <Text style={styles.qty}>
              Qty
            </Text>

            <Text style={styles.hargaSatuan}>
              Harga Satuan
            </Text>

            <Text style={styles.jumlah}>
              Jumlah
            </Text>
          </View>

          <View style={{ display: 'flex', flexDirection: "row", flexWrap: "nowrap" }}>
            <Text
              style={{ width: widthNo, borderRight: '1px solid #000', paddingLeft: '4px', paddingRight: '4px', paddingTop: '2px', paddingBottom: '1px' }}>
              &nbsp;
            </Text>

            <Text
              style={[styles.xxx, { borderRight: '1px solid #000', paddingLeft: '4px', paddingRight: '4px', paddingTop: '3px', paddingBottom: '1px', fontFamily: 'calibri-bold' }]}>
              {Array.isArray(stateDataProject) && (stateDataProject.length > 0) ? stateDataProject[0].barang_nama : ''}
            </Text>

            <Text
              style={{ width: widthQty, textAlign: 'right', borderRight: '1px solid #000', paddingLeft: '4px', paddingRight: '4px', paddingTop: '2px', paddingBottom: '1px' }}>
              &nbsp;
            </Text>

            <Text
              style={{ width: widthHargaSatuan, textAlign: 'right', borderRight: '1px solid #000', paddingLeft: '4px', paddingRight: '4px', paddingTop: '2px', paddingBottom: '1px' }}>
              &nbsp;
            </Text>

            <Text
              style={{ width: widthJumlah, textAlign: 'right', paddingLeft: '4px', paddingRight: '4px', paddingTop: '2px', paddingBottom: '1px' }}>
              &nbsp;
            </Text>
          </View>

          {Array.isArray(stateData) && (stateData.length > 0) ? Object.keys(stateData).map((keyDT, indexDT) => {
            return (
              <View
                key={keyDT}
                style={{ display: 'flex', flexDirection: "row", flexWrap: "nowrap" }}
              >
                <Text style={{ width: widthNo, borderRight: '1px solid #000', paddingLeft: '4px', paddingRight: '4px', paddingTop: '2px', paddingBottom: '1px' }}>
                  {Number(indexDT) + 1}
                </Text>

                <Text style={[styles.xxx, { borderRight: '1px solid #000', paddingLeft: '4px', paddingRight: '4px', paddingTop: '2px', paddingBottom: '1px', }]}>
                  {String(stateData[keyDT].item_jasa).split('')}
                </Text>

                <Text style={{ width: widthQty, textAlign: 'right', borderRight: '1px solid #000', paddingLeft: '4px', paddingRight: '4px', paddingTop: '2px', paddingBottom: '1px' }}>
                  {stateData[keyDT].qty ? numberWithComma(stateData[keyDT].qty) : ' - '}
                </Text>

                <Text style={{ width: widthHargaSatuan, textAlign: 'right', borderRight: '1px solid #000', paddingLeft: '4px', paddingRight: '4px', paddingTop: '2px', paddingBottom: '1px' }}>
                  Rp {stateData[keyDT].price ? numberWithComma(stateData[keyDT].price) : ' - '}
                </Text>

                <Text style={{ width: widthJumlah, textAlign: 'right', paddingLeft: '4px', paddingRight: '4px', paddingTop: '2px', paddingBottom: '1px' }}>
                  Rp {stateData[keyDT].jumlah ? numberWithComma(stateData[keyDT].jumlah) : ' - '}
                </Text>
              </View>
            )
          })
            :
            <View
              style={{ display: 'flex', flexDirection: "row", flexWrap: "nowrap" }}
            >
              <Text style={{ width: widthNo, borderRight: '1px solid #000', padding: '4px' }}>
                -
              </Text>

              <Text style={{ flexBasis: 'auto', flexGrow: '1', flex: 1, borderRight: '1px solid #000', padding: '4px' }}>
                -
              </Text>

              <Text style={{ width: widthQty, textAlign: 'right', borderRight: '1px solid #000', padding: '4px' }}>
                -
              </Text>

              <Text style={{ width: widthHargaSatuan, textAlign: 'right', borderRight: '1px solid #000', padding: '4px' }}>
                -
              </Text>

              <Text style={{ width: widthJumlah, textAlign: 'right', padding: '4px' }}>
                -
              </Text>
            </View>
          }
        </View>

        <View
          wrap={false}
          style={{ marginTop: '8px' }}
        >
          <Text style={{ fontFamily: 'calibri-bold' }}>
            Catatan:
          </Text>

          <Points
            stateDesc={pusatCabangActive.rules.one}
          />

          <Points
            stateDesc={pusatCabangActive.rules.two}
          />

          <Points
            stateDesc={pusatCabangActive.rules.three}
          />

          <Points
            stateDesc={pusatCabangActive.rules.five}
          />

          <Points
            stateDesc={pusatCabangActive.rules.six}
            stateIsBold={true}
          />
        </View>

        <View
          wrap={false}
          style={{ display: 'flex', flexDirection: "row", flexWrap: "nowrap", marginTop: '8px' }}
        >
          <View style={{ flexBasis: 'auto', flexGrow: '1', flex: 1, display: 'flex', flexDirection: "row", flexWrap: "nowrap" }}>
            <View style={{ flexBasis: 'auto', flexGrow: '1', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Text>
                Diterima oleh,
              </Text>
            </View>

            <View style={{ flexBasis: 'auto', flexGrow: '1', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <View>
                <Text>
                  Mengetahui,
                </Text>
              </View>

              <View>
                <ImagePdf src={ttdPerusahaan} style={{ width: '70px' }} />
              </View>

              <View>
                <Text>
                  Hans Adrian
                </Text>
              </View>
            </View>
          </View>

          <View style={{ width: widthHargaSatuan }}>
            <Text style={{ textAlign: 'left', paddingLeft: '4px', paddingRight: '4px', paddingBottom: '2px' }}>
              Sub Total
            </Text>

            <Text style={{ textAlign: 'left', paddingLeft: '4px', paddingRight: '4px', paddingBottom: '2px' }}>
              Uang Muka
            </Text>

            {fnTotal(stateData, Array.isArray(stateDataProject) && (stateDataProject.length > 0) ? (stateDataProject[0].diskon ? stateDataProject[0].diskon : 0) : 0, Array.isArray(stateDataProject) && (stateDataProject.length > 0) ? (stateDataProject[0].biaya_dp ? stateDataProject[0].biaya_dp : 0) : 0, stateDataProject).diskonVoucher > 0 &&
              <Text style={{ textAlign: 'left', paddingLeft: '4px', paddingRight: '4px', paddingBottom: '2px' }}>
                Potongan
              </Text>
            }

            <Text style={{ textAlign: 'left', paddingLeft: '4px', paddingRight: '4px', paddingBottom: '2px' }}>
              Diskon
            </Text>

            <Text style={{ textAlign: 'left', paddingLeft: '4px', paddingRight: '4px', paddingBottom: '2px', fontFamily: 'calibri-bold' }}>
              Total {Array.isArray(stateDataProject) && (stateDataProject.length > 0) ? (Boolean(Number(stateDataProject[0].is_lunas)) ? '(Lunas)' : '(Belum Lunas)') : '(Belum Lunas)'}
            </Text>
          </View>

          <View style={{ width: widthJumlah }}>
            <Text style={{ textAlign: 'right', paddingLeft: '4px', paddingRight: '4px', paddingBottom: '2px' }}>
              Rp {numberWithComma(fnSubTotal(stateData))}
            </Text>

            <Text style={{ textAlign: 'right', paddingLeft: '4px', paddingRight: '4px', paddingBottom: '2px' }}>
              Rp {Array.isArray(stateDataProject) && (stateDataProject.length > 0) ? (stateDataProject[0].biaya_dp ? numberWithComma(stateDataProject[0].biaya_dp) : ' - ') : ' - '}
            </Text>

            {fnTotal(stateData, Array.isArray(stateDataProject) && (stateDataProject.length > 0) ? (stateDataProject[0].diskon ? stateDataProject[0].diskon : 0) : 0, Array.isArray(stateDataProject) && (stateDataProject.length > 0) ? (stateDataProject[0].biaya_dp ? stateDataProject[0].biaya_dp : 0) : 0, stateDataProject).diskonVoucher > 0 &&
              <Text style={{ textAlign: 'right', paddingLeft: '4px', paddingRight: '4px', paddingBottom: '2px' }}>
                Rp {numberWithComma(fnTotal(stateData, Array.isArray(stateDataProject) && (stateDataProject.length > 0) ? (stateDataProject[0].diskon ? stateDataProject[0].diskon : 0) : 0, Array.isArray(stateDataProject) && (stateDataProject.length > 0) ? (stateDataProject[0].biaya_dp ? stateDataProject[0].biaya_dp : 0) : 0, stateDataProject).diskonVoucher)}
              </Text>
            }

            <Text style={{ textAlign: 'right', paddingLeft: '4px', paddingRight: '4px', paddingBottom: '2px' }}>
              Rp {Array.isArray(stateDataProject) && (stateDataProject.length > 0) ? (stateDataProject[0].diskon ? numberWithComma(stateDataProject[0].diskon) : ' - ') : ' - '}
            </Text>

            <Text style={{ textAlign: 'right', paddingLeft: '4px', paddingRight: '4px', paddingBottom: '2px', fontFamily: 'calibri-bold' }}>
              Rp {numberWithComma(fnTotal(stateData, Array.isArray(stateDataProject) && (stateDataProject.length > 0) ? (stateDataProject[0].diskon ? stateDataProject[0].diskon : 0) : 0, Array.isArray(stateDataProject) && (stateDataProject.length > 0) ? (stateDataProject[0].biaya_dp ? stateDataProject[0].biaya_dp : 0) : 0, stateDataProject).total)}
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  )

  const ButtonView = ({ stateDisabled, stateCaption, stateClassName, fnClick }) => {
    return (
      <Button
        variant='filled'
        size='xs'
        disabled={stateDisabled}
        onClick={fnClick}
        className={stateClassName}

        sx={(tema) => ({
          '&[data-disabled="true"]': {
            backgroundColor: tema.colors.blue[3],
            opacity: 0.5,
          }
        })}

        styles={{
          label: {
            textTransform: 'uppercase'
          },
        }}
      >
        {stateCaption}
      </Button>
    )
  }

  return (
    <React.Fragment>
      {Array.isArray(stateData) && (stateData.length > 0) ?
        <BlobProvider document={DocPdf}>
          {({ url }) => {
            if (!url) {
              return <ButtonView
                stateDisabled={true}
                stateCaption='Loading..'
                stateClassName='cursorProgress'
                fnClick={() => { }}
              />
            } else {
              return (
                <Box>
                  {stateDT.jenisDokumen === utils.jenisDokumen.invoice.value &&
                    <PickerDate
                      stateMinDate={null}
                      stateDate={data.firstPrintInvTanggalSelected}
                      fnSetDate={(val) => {
                        setData(d => ({
                          ...d,
                          firstPrintInvTanggalClicked: val,
                          firstPrintInvTanggalSelected: d.firstPrintInvTanggalPermanent ? val : d.firstPrintInvTanggalSelected,
                          firstPrintInvModal: d.firstPrintInvTanggalPermanent ? false : true
                        }))
                      }}

                      stateVariant="default"
                      stateClearable={false}

                      stateStylesInput={{
                        backgroundColor: theme.colorScheme === 'light' ? '#fff' : theme.colors.dark[7]
                      }}

                      stateLabel={
                        <React.Fragment>
                          <Group spacing={6}>
                            <TextMantine>Tanggal Cetak {capitalizeEachWord(stateCaption)}</TextMantine>
                            {data.firstPrintInvSubmitLoading && <Loader size="xs" />}
                          </Group>
                        </React.Fragment>
                      }
                      statePlaceHolder={`Tanggal Cetak ${capitalizeEachWord(stateCaption)}`}
                      stateWithAsterisk={false}
                      stateDescription={data.firstPrintInvTanggalPermanent ? `Tanggal cetak ${capitalizeEachWord(stateCaption)} pertama: ${isoDateToHumanDate({
                        stateDate: data.firstPrintInvTanggalPermanent,
                        stateLang: 'id',
                        stateIsShort: false,
                        stateDay: false,
                        stateDefaultValue: ' - ',
                        stateDateType: 'day'
                      })}` : ''}
                      stateError=''

                      stateMB="sm"
                    />
                  }

                  {((data.firstPrintInvTanggalSelected && (stateDT.jenisDokumen === utils.jenisDokumen.invoice.value)) || (stateDT.jenisDokumen === utils.jenisDokumen.buktiBayar.value)) &&
                    <Group spacing='xs' noWrap>
                      <Button
                        variant='filled'
                        size='xs'
                        mr='sm'
                        leftIcon={<IconDownload size='1rem' />}
                        onClick={() => {
                          saveAs(url, `${stateCaption} ${Array.isArray(stateDataProject) && (stateDataProject.length > 0) ? stateDataProject[0].project_id : 'DOCUMENT'}.pdf`);
                          fnSetIdleData();
                        }}
                      >
                        Simpan
                      </Button>

                      <Button
                        variant='filled'
                        size='xs'
                        color="green"
                        px='xl'
                        leftIcon={<IconFileDescription size='1rem' />}
                        onClick={() => {
                          window.open(url);
                          fnSetIdleData();
                        }}
                      >
                        Lihat
                      </Button>
                    </Group>
                  }
                </Box>
              )
            }
          }}
        </BlobProvider>
        :
        <ButtonView
          stateDisabled={stateLoading}
          stateCaption={stateLoading ? 'Loading..' : `Generate ${capitalizeEachWord(stateCaption)}`}
          stateClassName={stateLoading ? 'cursorProgress' : ''}
          fnClick={() => {
            fnGetData()
          }}
        />
      }

      <ModalResponse
        stateShow={data.modalResponse.show}
        fnHide={() => setData(d => ({
          ...d,
          modalResponse: {
            ...d.modalResponse,
            show: false
          }
        }))}

        stateHideOnClickOutSide={data.modalResponse.hideOnClickOutSide}
        stateType={data.modalResponse.type}
        stateContent={data.modalResponse.content}
      />

      <Modal.Root
        opened={data.firstPrintInvModal}
        onClose={() => setData(d => ({ ...d, firstPrintInvModal: false }))}
        closeOnClickOutside={true}
        size='md'
        centered

        styles={() => ({ overlay: { zIndex: 201 } })}
      >
        <Modal.Overlay />

        <Modal.Content
          scrollAreaComponent={ScrollArea.Autosize}
        >
          <Modal.Header>
            <Modal.Title sx={{ fontWeight: '500' }}>{String('Confirmation').toUpperCase()}</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <TextMantine>
              Jadikan tanggal {isoDateToHumanDate({
                stateDate: data.firstPrintInvTanggalClicked,
                stateLang: 'id',
                stateIsShort: false,
                stateDay: false,
                stateDefaultValue: ' - ',
                stateDateType: 'day'
              })} sebagai default tanggal {capitalizeEachWord(stateCaption)} untuk nota ini ?
            </TextMantine>

            <TextMantine size='xs' color="dimmed" mt='xs'>
              Keterangan: Tanggal {isoDateToHumanDate({
                stateDate: data.firstPrintInvTanggalClicked,
                stateLang: 'id',
                stateIsShort: false,
                stateDay: false,
                stateDefaultValue: ' - ',
                stateDateType: 'day'
              })} akan disimpan sebagai tanggal {capitalizeEachWord(stateCaption)} pertama dan menjadi default untuk nota ini, namun Anda masih dapat memilih tanggal lain saat mencetak {capitalizeEachWord(stateCaption)} berikutnya.
            </TextMantine>
          </Modal.Body>

          <Modal.Header sx={{ bottom: 0 }}>
            <Box sx={{ width: '100%' }}>
              <Group position='right'>
                <Button
                  onClick={() => setData(d => ({
                    ...d,
                    firstPrintInvTanggalSelected: d.firstPrintInvTanggalClicked,
                    firstPrintInvModal: false
                  }))}
                  size='xs'
                  variant='light'
                  px='md'
                >
                  Tidak
                </Button>

                <Button
                  size='xs'
                  variant='filled'
                  loaderPosition="right"
                  px='xs'
                  loading={data.firstPrintInvSubmitLoading}
                  onClick={handleFirstPrintInvDate}
                >
                  Ya, jadikan default
                </Button>
              </Group>
            </Box>
          </Modal.Header>
        </Modal.Content>
      </Modal.Root>
    </React.Fragment>
  )
}

export default Invoice;